import * as React from "react";
import * as R from "ramda";
import { debounce } from "lodash";
import { v4 as uuid } from "uuid";
import ResizeObserver from "resize-observer-polyfill";
import { useLocation, useHistory } from "react-router-dom";

import { isDev } from "../env";
import { PrimitiveAtom, atom, useAtom } from "jotai";
import { useMutation } from "@tanstack/react-query";
import request from "./request";

import { downloadFile, QUERY_PARAM_DELIMITER } from "./util";
import { useMemo } from "react";

export const useUUID = () => React.useState(() => uuid())[0];

export const useClientRect = (): [any, any] => {
  const [node, setNode] = React.useState<any>(null);
  const [rect, setRect] = React.useState({});

  const resizeObserver = React.useMemo(
    () =>
      new ResizeObserver((entries) => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        // Since we only observe the one element, we don't need to loop over the array
        const entry = entries[0]!;
        setRect(entry.contentRect);
      }),
    []
  );

  React.useEffect(() => {
    if (node) {
      resizeObserver.observe(node);
      return () => resizeObserver.unobserve(node);
    }
  }, [node, resizeObserver]);

  return [rect, setNode];
};

export const useFileBrowser = (
  acceptedFileTypes: Array<string> | string,
  handleFileSelect: (arg1: FileList) => void | Promise<void>,
  fileBrowserDataTestId: string = "use-file-browser-input"
): [() => void] => {
  const ref = React.useRef<HTMLInputElement | null>(null);

  const openFileBrowser = () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  React.useEffect(() => {
    const onClickHandler = (e: any) => (e.target.value = "");
    const onChangeHandler = (e: any) => {
      handleFileSelect(e.target.files)?.catch((error) => {
        if (isDev) {
          // eslint-disable-next-line
          console.error("Failed to select file: ", error);
        }
      });
    };

    ref.current = document.createElement("input");
    ref.current.type = "file";

    ref.current.dataset.testid = fileBrowserDataTestId;

    ref.current.style.display = "none";

    ref.current.accept = Array.isArray(acceptedFileTypes)
      ? acceptedFileTypes.join(",")
      : acceptedFileTypes;
    ref.current.addEventListener("click", onClickHandler);
    ref.current.addEventListener("change", onChangeHandler);

    if (document.body) document.body.appendChild(ref.current);

    return () => {
      ref.current?.removeEventListener("click", onClickHandler);
      ref.current?.removeEventListener("change", onChangeHandler);
      if (document.body && ref.current) document.body.removeChild(ref.current);
    };
  }, [acceptedFileTypes, fileBrowserDataTestId, handleFileSelect]);

  return [openFileBrowser];
};

// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export const useInterval = (callback: () => void, delay: number): void => {
  const savedCallback = React.useRef<() => void>();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      if (savedCallback.current) savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};
/**
 * This function receives the click event during the capture phase
 */
export const useDidClickOutsideElement = (
  cb: (arg1: boolean) => void,
  condition = true,
  additionalRefs?: React.RefObject<HTMLElement>[]
): any => {
  const ref = React.useRef<HTMLElement>();
  React.useEffect(() => {
    if (condition) {
      const onClickHandler = (e: MouseEvent) => {
        // prettier-ignore
        if (ref.current && !ref.current.contains(e.target as Node) &&  (additionalRefs ?? []).every((r) => !r.current?.contains(e.target as Node))) { 
          cb(false);
        }
      };
      document.addEventListener("click", onClickHandler, { capture: true });

      return () => {
        document.removeEventListener("click", onClickHandler, {
          capture: true,
        });
      };
    }
  }, [cb, condition, additionalRefs]);

  return ref;
};

// Re-exported from ./util to keep existing `@src/global_functions/hooks`
// import sites working while avoiding a hooks <-> util dependency cycle.
export { QUERY_PARAM_DELIMITER };

export const useSearchParams = () => {
  const history = useHistory();
  const location = useLocation();

  const getAllParams = React.useCallback((): {
    [key: string]: string | null | undefined;
  } => {
    const output: Record<string, any> = {};
    const searchParams = new URLSearchParams(location.search);
    for (const [key, val] of searchParams.entries()) {
      // not supporting recurring search keys currently b/c no current need
      output[key] = val;
    }
    return output;
  }, [location.search]);

  const getParsedParam = React.useCallback(
    <T>(
      key: string,
      parser: (arg1: string) => T | null | undefined
    ): T | null | undefined => {
      const searchParams = new URLSearchParams(location.search);
      const incomingParam = searchParams.get(key);
      if (incomingParam == null) return null;
      const parsedParam = parser(incomingParam);
      if (incomingParam != null && parsedParam == null) {
        searchParams.delete(key);
        history.replace({
          pathname: location.pathname,
          search: "?" + searchParams.toString(),
        });
      }
      return parsedParam;
    },
    [history, location.pathname, location.search]
  );

  const getAllParsedParams = React.useCallback(
    (keyParserPairs: {
      [key: string]: (arg1: string) => any;
    }): {
      [key: string]: any;
    } => {
      let hasInvalidParam = false;
      const searchParams = new URLSearchParams(location.search);
      for (const [key, val] of searchParams.entries()) {
        if (keyParserPairs[key] == null || keyParserPairs[key]!(val) == null) {
          hasInvalidParam = true;
          searchParams.delete(key);
        }
      }
      if (hasInvalidParam) {
        history.replace({
          pathname: location.pathname,
          search: "?" + searchParams.toString(),
        });
      }
      return R.mapObjIndexed((parser, key) => {
        const paramVal = searchParams.get(key);
        if (paramVal == null) return null;
        return parser(paramVal);
      }, keyParserPairs);
    },
    [history, location.pathname, location.search]
  );

  const upsertSearchParam = React.useCallback(
    (key: string, value: string | false): void => {
      // must use history.location.search because location.search is only updated after the following render
      // so if we try to update multiple params at once, only the last one will be updated
      // since history.location.search is updated within this function, it will stay up to date even between renders
      const searchParams = new URLSearchParams(history.location.search);
      if (!key) return;
      if (!value) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      history.replace({
        search: "?" + searchParams.toString(),
      });
    },
    [history]
  );

  const removeSearchParams = React.useCallback(
    (keys: string | Array<string>): void => {
      const searchParams = new URLSearchParams(history.location.search);
      if (Array.isArray(keys)) {
        keys.forEach((key) => {
          searchParams.delete(key);
        });
      } else {
        searchParams.delete(keys);
      }

      history.replace({
        search: "?" + searchParams.toString(),
      });
    },
    [history]
  );

  const clearSearchParams = React.useCallback((): void => {
    history.replace({
      pathname: location.pathname,
      search: "",
    });
  }, [history, location.pathname]);

  return {
    getAllParams,
    getParsedParam,
    getAllParsedParams,
    upsertSearchParam,
    removeSearchParams,
    clearSearchParams,
  };
};

function createQueryParamString(value: any): string | false {
  if (!value) return false;
  if (Array.isArray(value)) {
    return value.join(QUERY_PARAM_DELIMITER);
  }
  return value.toString();
}

const SEARCH_PARAM_ATOMS: { [key: string]: PrimitiveAtom<any> } = {};

// Allows multiple components to use this hook and share state based on key
export function useSearchParamAtom<T>(
  key: string,
  parser: (arg1: string) => T | null | undefined
) {
  const { getParsedParam, upsertSearchParam } = useSearchParams();

  const parsedParam = React.useMemo(
    () => getParsedParam(key, parser),
    [key, parser, getParsedParam]
  );

  if (!SEARCH_PARAM_ATOMS[key]) {
    SEARCH_PARAM_ATOMS[key] = atom(parsedParam ?? undefined);
  }

  const [state, _setState] = useAtom(
    SEARCH_PARAM_ATOMS[key] as PrimitiveAtom<T | null | undefined>
  );

  React.useEffect(() => {
    _setState(parsedParam);
  }, [parsedParam, _setState]);

  const setState = React.useCallback(
    (newState: any) => {
      if (isFunction(newState)) {
        return _setState((prevState) => {
          const updatedState = newState(prevState);
          return updatedState;
        });
      }

      upsertSearchParam(key, createQueryParamString(newState));
      return _setState(newState);
    },
    [_setState, upsertSearchParam, key]
  );

  return [state as T | null | undefined, setState] as const;
}

export function useMultipleSearchParams<
  KeyType extends string,
  ParsersRecord extends Record<KeyType, (val: string) => any>
>(
  keysAndParsers: ParsersRecord,
  ignoreKeysAndParsers?: Record<string, (val: string) => any>
) {
  const { upsertSearchParam, clearSearchParams, getAllParsedParams } =
    useSearchParams();

  const parsedParams = useMemo(
    () => getAllParsedParams({ ...keysAndParsers, ...ignoreKeysAndParsers }),
    [getAllParsedParams, keysAndParsers, ignoreKeysAndParsers]
  );

  const setParam = React.useCallback(
    (key: KeyType, value: string | null) => {
      upsertSearchParam(key, value ?? false);
    },
    [upsertSearchParam]
  );

  const clearParams = React.useCallback(() => {
    const ignoreParams = Object.entries(parsedParams).filter(([key]) =>
      Object.keys(ignoreKeysAndParsers ?? {}).includes(key)
    );
    clearSearchParams();
    ignoreParams.forEach(([key, value]) => upsertSearchParam(key, value));
  }, [
    clearSearchParams,
    ignoreKeysAndParsers,
    parsedParams,
    upsertSearchParam,
  ]);

  const params = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(parsedParams).filter(([key]) =>
          Object.keys(keysAndParsers).includes(key)
        )
      ) as {
        [K in keyof ParsersRecord]: ReturnType<ParsersRecord[K]>;
      },
    [keysAndParsers, parsedParams]
  );

  return {
    params,
    setParam: setParam as (
      key: keyof ParsersRecord,
      value: string | null
    ) => void,
    clearParams,
  };
}

export function useSearchParamStateWithDefault<T>(
  key: string,
  parser: (arg1: string) => T,
  defaultValue: T
) {
  const { getParsedParam, upsertSearchParam } = useSearchParams();
  const [state, _setState] = React.useState(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- a falsy parsed value should fall back to the default (preserves existing behavior)
    getParsedParam(key, parser) || defaultValue
  );

  const upsertSearchParamState = React.useCallback(
    (value: T) => {
      if (value === defaultValue) {
        upsertSearchParam(key, false);
      } else {
        upsertSearchParam(key, createQueryParamString(value));
      }
      return value;
    },
    [upsertSearchParam, key, defaultValue]
  );

  const setState: React.Dispatch<React.SetStateAction<T>> = React.useCallback(
    (newState) => {
      if (isFunction(newState)) {
        return _setState((prevState) => {
          const updatedState = newState(prevState);
          return upsertSearchParamState(updatedState);
        });
      }

      upsertSearchParamState(newState);
      return _setState(newState);
    },
    [_setState, upsertSearchParamState]
  );

  return [state, setState] as const;
}

function isFunction<T>(
  value: T | ((prevState: T) => T)
): value is (prevState: T) => T {
  return typeof value === "function";
}

export function useStorage<T>(
  key: string,
  initialValue: T,
  storage = window.localStorage
) {
  const [storedValue, setStoredValue] = React.useState<T>(() => {
    try {
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error: any) {
      console.log(error); // eslint-disable-line
      return initialValue;
    }
  });

  const setValue = React.useCallback(
    (value: T) => {
      try {
        setStoredValue(value);
        if (value === undefined) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, JSON.stringify(value));
        }
      } catch (error: any) {
        console.log(error); // eslint-disable-line
      }
    },
    [key, storage]
  );

  return [storedValue, setValue] as const;
}

export const useCopyToClipboard = ({
  resetInterval,
}: { resetInterval?: number | null } = {}) => {
  const [isCopied, setIsCopied] = React.useState<boolean>(false);

  const handleCopy = React.useCallback(async (text: string | number) => {
    await navigator.clipboard.writeText(text.toString());
    setIsCopied(true);
  }, []);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isCopied && resetInterval) {
      timeout = setTimeout(() => setIsCopied(false), resetInterval);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isCopied, resetInterval]);

  return { isCopied, handleCopy, setIsCopied };
};

/*
 * If value is defined, state is considered controlled and value is passed through
 * If value is undefined, state is considered uncontrolled and is identical to useState
 */
export function useControllableState<T>(value: T, defaultValue: T) {
  const [storedValue, setStoredValue] = React.useState(value || defaultValue);
  const isControlled = value !== undefined;
  const wasControlledRef = React.useRef(isControlled);
  const derivedValue = isControlled ? value : storedValue;
  const setValue: React.Dispatch<React.SetStateAction<T>> = React.useCallback(
    (newValue) => {
      setStoredValue(newValue);
    },
    []
  );

  if (wasControlledRef.current !== isControlled) {
    // eslint-disable-next-line no-console
    console.warn(
      "Warning: A component is changing a controlled input to be uncontrolled. This is likely caused by the value changing from a defined to undefined, which should not happen. Decide between using a controlled or uncontrolled input element for the lifetime of the component."
    );
    wasControlledRef.current = isControlled;
  }

  return [derivedValue, setValue] as const;
}

export function usePrevious<T>(state: T): T | undefined {
  const ref = React.useRef<T>();

  React.useEffect(() => {
    ref.current = state;
  }, [state]);

  return ref.current;
}

export function useMergedRef<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
  return React.useCallback(
    (element: T) => {
      refs.forEach((ref) => {
        if (typeof ref === "function") {
          ref(element);
        } else if (ref && typeof ref === "object") {
          (ref as React.MutableRefObject<T>).current = element;
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs
  );
}

export const useDownloadFile = (
  url: string,
  {
    filename,
    type,
  }: {
    filename: string;
    type: string;
  }
) =>
  useMutation({
    mutationFn: async (params: { [key: string]: any } = {}) => {
      const response = await request.get(url).query(params);
      // response.text probably doesnt cover all bases. May have to update to handle response.body / response.buffer etc to make it more robust
      const blob = new Blob([response.text], { type });
      return downloadFile({
        blob,
        filename,
      });
    },
  });

export function useEffectOnce(effect: React.EffectCallback) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(effect, []);
}

/**
 * BackpackPageWrapper must wrap any components that call this hook
 */
export function useScrollToTopOnMountEffect() {
  const { hash } = useLocation();

  // scroll to top of page when component mounts
  useEffectOnce(() => {
    const mainContent = document.getElementsByTagName("main")[0];
    if (mainContent && !hash) {
      mainContent.scrollTop = 0;
    }
  });
}

export function useDebounce<T>(value: T, wait = 500) {
  const [debounceValue, setDebounceValue] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceValue(value);
    }, wait);
    return () => clearTimeout(timer); // cleanup when unmounted
  }, [value, wait]);

  return debounceValue;
}

type ScrollPositionAtom = { position: number; resetId?: number };
const SCROLL_POSITION_ATOMS: {
  [key: string]: PrimitiveAtom<ScrollPositionAtom>;
} = {};
export function useScrollRestoration(
  key: string,
  {
    resetId,
    enabled = true,
    getScrollElement,
  }: {
    enabled?: boolean;
    getScrollElement: () => HTMLElement | undefined | null;
    resetId?: number;
  }
) {
  if (!SCROLL_POSITION_ATOMS[key]) {
    SCROLL_POSITION_ATOMS[key] = atom<ScrollPositionAtom>({
      position: 0,
      resetId,
    });
  }

  const [scrollPosition, setScrollPosition] = useAtom(
    SCROLL_POSITION_ATOMS[key]!
  );

  const scrollPositionRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!enabled) return;
    const element = getScrollElement();
    const handleScroll = debounce(() => {
      if (element) {
        scrollPositionRef.current = element.scrollTop;
      }
    }, 200);
    element?.addEventListener("scroll", handleScroll);
    return () => {
      element?.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [enabled, getScrollElement]);

  const { resetId: storedResetId, position } = scrollPosition;

  React.useEffect(() => {
    if (!enabled) return;
    const element = getScrollElement();
    if (element) {
      if (storedResetId !== resetId) {
        element.scrollTop = 0;
        scrollPositionRef.current = 0;
        setScrollPosition({ resetId, position: 0 });
      } else {
        element.scrollTop = position;
        scrollPositionRef.current = position;
      }
    }
  }, [
    getScrollElement,
    key,
    resetId,
    storedResetId,
    position,
    enabled,
    setScrollPosition,
  ]);

  React.useEffect(() => {
    return () => {
      setScrollPosition((prev) => ({
        ...prev,
        position: scrollPositionRef.current,
      }));
    };
  }, [setScrollPosition]);
}
